import { useState, useEffect, useRef } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Cell
} from "recharts";

// ─────────────────────────────────────────────────────────────────────────────
// STRATEGIC FRAMING NOTE (for demo presenter):
//
// NLA does NOT scan player faces directly. NLA REGULATES whether operators
// are running compliant KYC and age-verification programmes on their players.
// This panel shows NLA's enforcement view of each operator's compliance posture.
// ─────────────────────────────────────────────────────────────────────────────

const OPERATORS = [
  {
    id: 1,
    name: "Bet360 Liberia",
    type: "Sports Betting",
    kycProgram: "IMPLEMENTED",
    ageVerificationMethod: "National ID + Selfie Match",
    verifiedPlayerRate: 96.2,
    unverifiedActiveAccounts: 14,
    underageFlags: 0,
    avgOnboardingTime: "3.2 min",
    kycProvider: "Internal + MoJ Database",
    lastAuditDate: "Dec 15, 2025",
    lastAuditResult: "PASS",
    policyVersion: "v3.1",
    recentIncidents: [],
    monthlyKYC: [
      { month: "Jul", rate: 91 }, { month: "Aug", rate: 93 }, { month: "Sep", rate: 94 },
      { month: "Oct", rate: 95 }, { month: "Nov", rate: 96 }, { month: "Dec", rate: 96.2 },
    ],
    ageBreakdown: [
      { group: "18–24", count: 1240, verified: 1196 },
      { group: "25–34", count: 3210, verified: 3145 },
      { group: "35–44", count: 2180, verified: 2148 },
      { group: "45–54", count: 980, verified: 972 },
      { group: "55+", count: 440, verified: 439 },
    ],
    controls: [
      { label: "Age gate at registration", status: true },
      { label: "National ID verification", status: true },
      { label: "Liveness / selfie check", status: true },
      { label: "Ministry of Justice cross-ref", status: true },
      { label: "Re-verification on suspicious activity", status: true },
      { label: "Session limits for new accounts", status: true },
    ],
  },
  {
    id: 2,
    name: "Lucky Star Casino",
    type: "Online Casino",
    kycProgram: "DEFICIENT",
    ageVerificationMethod: "Self-declaration only",
    verifiedPlayerRate: 44.1,
    unverifiedActiveAccounts: 1872,
    underageFlags: 7,
    avgOnboardingTime: "0.4 min",
    kycProvider: "None (manual check)",
    lastAuditDate: "Aug 14, 2024",
    lastAuditResult: "FAIL",
    policyVersion: "v1.0 (outdated)",
    recentIncidents: [
      "3 accounts flagged as potentially underage — Dec 10",
      "KYC bypass pathway discovered — Dec 3",
      "4 accounts registered with identical selfie hash — Nov 28",
    ],
    monthlyKYC: [
      { month: "Jul", rate: 38 }, { month: "Aug", rate: 40 }, { month: "Sep", rate: 39 },
      { month: "Oct", rate: 41 }, { month: "Nov", rate: 43 }, { month: "Dec", rate: 44.1 },
    ],
    ageBreakdown: [
      { group: "18–24", count: 890, verified: 392 },
      { group: "25–34", count: 1240, verified: 547 },
      { group: "35–44", count: 680, verified: 300 },
      { group: "45–54", count: 310, verified: 137 },
      { group: "55+", count: 140, verified: 62 },
    ],
    controls: [
      { label: "Age gate at registration", status: true },
      { label: "National ID verification", status: false },
      { label: "Liveness / selfie check", status: false },
      { label: "Ministry of Justice cross-ref", status: false },
      { label: "Re-verification on suspicious activity", status: false },
      { label: "Session limits for new accounts", status: true },
    ],
  },
  {
    id: 3,
    name: "Galaxy Gaming",
    type: "Online Casino",
    kycProgram: "SUSPENDED",
    ageVerificationMethod: "N/A — operations suspended",
    verifiedPlayerRate: 0,
    unverifiedActiveAccounts: 0,
    underageFlags: 0,
    avgOnboardingTime: "N/A",
    kycProvider: "N/A",
    lastAuditDate: "May 20, 2023",
    lastAuditResult: "FAIL — LICENCE REVOKED",
    policyVersion: "N/A",
    recentIncidents: [
      "Operation suspended Dec 28, 2025",
      "Unlicensed game types linked to underage access patterns",
      "KYC process completely absent — confirmed during FIA inspection",
    ],
    monthlyKYC: [
      { month: "Jul", rate: 12 }, { month: "Aug", rate: 14 }, { month: "Sep", rate: 11 },
      { month: "Oct", rate: 10 }, { month: "Nov", rate: 0 }, { month: "Dec", rate: 0 },
    ],
    ageBreakdown: [],
    controls: [
      { label: "Age gate at registration", status: false },
      { label: "National ID verification", status: false },
      { label: "Liveness / selfie check", status: false },
      { label: "Ministry of Justice cross-ref", status: false },
      { label: "Re-verification on suspicious activity", status: false },
      { label: "Session limits for new accounts", status: false },
    ],
  },
  {
    id: 4,
    name: "Premier Lotto",
    type: "National Lottery",
    kycProgram: "IMPLEMENTED",
    ageVerificationMethod: "National ID + Agent verification",
    verifiedPlayerRate: 88.4,
    unverifiedActiveAccounts: 231,
    underageFlags: 1,
    avgOnboardingTime: "5.1 min",
    kycProvider: "NLA Agent Network",
    lastAuditDate: "Sep 10, 2025",
    lastAuditResult: "PASS",
    policyVersion: "v2.4",
    recentIncidents: [
      "1 minor identified via NLA agent report — Nov 15 (resolved)",
    ],
    monthlyKYC: [
      { month: "Jul", rate: 82 }, { month: "Aug", rate: 84 }, { month: "Sep", rate: 85 },
      { month: "Oct", rate: 86 }, { month: "Nov", rate: 87 }, { month: "Dec", rate: 88.4 },
    ],
    ageBreakdown: [
      { group: "18–24", count: 540, verified: 477 },
      { group: "25–34", count: 820, verified: 724 },
      { group: "35–44", count: 640, verified: 566 },
      { group: "45–54", count: 310, verified: 274 },
      { group: "55+", count: 190, verified: 168 },
    ],
    controls: [
      { label: "Age gate at registration", status: true },
      { label: "National ID verification", status: true },
      { label: "Liveness / selfie check", status: false },
      { label: "Ministry of Justice cross-ref", status: true },
      { label: "Re-verification on suspicious activity", status: true },
      { label: "Session limits for new accounts", status: true },
    ],
  },
  {
    id: 5,
    name: "Monrovia Bet",
    type: "Sports Betting",
    kycProgram: "PARTIAL",
    ageVerificationMethod: "National ID (no liveness check)",
    verifiedPlayerRate: 71.8,
    unverifiedActiveAccounts: 422,
    underageFlags: 2,
    avgOnboardingTime: "2.1 min",
    kycProvider: "Internal",
    lastAuditDate: "Oct 22, 2025",
    lastAuditResult: "CONDITIONAL PASS",
    policyVersion: "v2.0",
    recentIncidents: [
      "2 accounts flagged via behavioural pattern analysis — Dec 8",
    ],
    monthlyKYC: [
      { month: "Jul", rate: 64 }, { month: "Aug", rate: 66 }, { month: "Sep", rate: 68 },
      { month: "Oct", rate: 70 }, { month: "Nov", rate: 71 }, { month: "Dec", rate: 71.8 },
    ],
    ageBreakdown: [
      { group: "18–24", count: 720, verified: 517 },
      { group: "25–34", count: 1100, verified: 790 },
      { group: "35–44", count: 640, verified: 460 },
      { group: "45–54", count: 280, verified: 201 },
      { group: "55+", count: 120, verified: 86 },
    ],
    controls: [
      { label: "Age gate at registration", status: true },
      { label: "National ID verification", status: true },
      { label: "Liveness / selfie check", status: false },
      { label: "Ministry of Justice cross-ref", status: false },
      { label: "Re-verification on suspicious activity", status: false },
      { label: "Session limits for new accounts", status: true },
    ],
  },
  {
    id: 6,
    name: "DoxxBet Liberia",
    type: "Online Casino / Sports Betting",
    kycProgram: "PARTIAL",
    ageVerificationMethod: "Passport scan (cross-border players unverified)",
    verifiedPlayerRate: 62.3,
    unverifiedActiveAccounts: 618,
    underageFlags: 3,
    avgOnboardingTime: "4.8 min",
    kycProvider: "Slovakia-based (not NLA-approved)",
    lastAuditDate: "Jul 1, 2025",
    lastAuditResult: "CONDITIONAL PASS",
    policyVersion: "v1.8",
    recentIncidents: [
      "KYC provider (Slovak) not on NLA-approved list — flagged Nov 30",
      "3 underage behavioural flags via cross-border accounts — Dec 5",
    ],
    monthlyKYC: [
      { month: "Jul", rate: 55 }, { month: "Aug", rate: 57 }, { month: "Sep", rate: 59 },
      { month: "Oct", rate: 60 }, { month: "Nov", rate: 61 }, { month: "Dec", rate: 62.3 },
    ],
    ageBreakdown: [
      { group: "18–24", count: 490, verified: 305 },
      { group: "25–34", count: 740, verified: 461 },
      { group: "35–44", count: 410, verified: 256 },
      { group: "45–54", count: 180, verified: 112 },
      { group: "55+", count: 80, verified: 50 },
    ],
    controls: [
      { label: "Age gate at registration", status: true },
      { label: "National ID verification", status: true },
      { label: "Liveness / selfie check", status: true },
      { label: "Ministry of Justice cross-ref", status: false },
      { label: "Re-verification on suspicious activity", status: false },
      { label: "Session limits for new accounts", status: true },
    ],
  },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const programConfig = {
  IMPLEMENTED: { color: "#52C41A", bg: "rgba(82,196,26,0.12)", border: "rgba(82,196,26,0.3)" },
  PARTIAL:     { color: "#FAAD14", bg: "rgba(250,173,20,0.12)", border: "rgba(250,173,20,0.3)" },
  DEFICIENT:   { color: "#FF7A00", bg: "rgba(255,122,0,0.12)", border: "rgba(255,122,0,0.3)" },
  SUSPENDED:   { color: "#FF4D4F", bg: "rgba(255,77,79,0.12)", border: "rgba(255,77,79,0.3)" },
};

const auditConfig = {
  "PASS":                   { color: "#52C41A" },
  "CONDITIONAL PASS":       { color: "#FAAD14" },
  "FAIL":                   { color: "#FF4D4F" },
  "FAIL — LICENCE REVOKED": { color: "#FF4D4F" },
};

// ─── ANIMATED RATE BAR ────────────────────────────────────────────────────────

function RateBar({ value, max = 100 }) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setW(value), 120);
    return () => clearTimeout(t);
  }, [value]);
  const color = value >= 85 ? "#52C41A" : value >= 60 ? "#FAAD14" : "#FF4D4F";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ flex: 1, height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{
          width: `${w}%`, height: "100%", background: color, borderRadius: 3,
          transition: "width 1.1s cubic-bezier(0.4,0,0.2,1)",
          boxShadow: `0 0 8px ${color}60`,
        }} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color, fontFamily: "'IBM Plex Mono', monospace", minWidth: 44 }}>
        {value.toFixed(1)}%
      </span>
    </div>
  );
}

// ─── MOCK FACIAL SCAN WIDGET ──────────────────────────────────────────────────
// This demonstrates what the operator's onboarding system sends to GCRMS:
// a verified/rejected token, not the raw face image.

function FacialScanDemo() {
  const [phase, setPhase] = useState("idle"); // idle | scanning | match | reject
  const [demo, setDemo] = useState(null);

  const SCENARIOS = [
    {
      label: "✓ Verified — Adult",
      name: "Emmanuel K.",
      dob: "1990-03-14",
      age: 35,
      idRef: "LIB-NID-881234",
      result: "VERIFIED",
      livenessScore: 98.4,
      ageConfidence: 99.1,
      matchScore: 97.2,
      outcome: "match",
    },
    {
      label: "⚠ Flagged — Age ambiguous",
      name: "S. Williams",
      dob: "2006-09-01",
      age: 19,
      idRef: "LIB-NID-992817",
      result: "MANUAL REVIEW",
      livenessScore: 94.1,
      ageConfidence: 61.4,
      matchScore: 89.3,
      outcome: "match",
    },
    {
      label: "✗ Rejected — Underage",
      name: "[REDACTED]",
      dob: "2010-01-22",
      age: 15,
      idRef: "LIB-NID-001122",
      result: "REJECTED",
      livenessScore: 91.2,
      ageConfidence: 97.8,
      matchScore: 95.1,
      outcome: "reject",
    },
  ];

  const run = (scenario) => {
    setPhase("scanning");
    setDemo(null);
    setTimeout(() => {
      setDemo(scenario);
      setPhase(scenario.outcome);
    }, 2200);
  };

  const resultColors = {
    VERIFIED: "#52C41A",
    "MANUAL REVIEW": "#FAAD14",
    REJECTED: "#FF4D4F",
  };

  return (
    <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#F0F0F0", marginBottom: 4 }}>
          Operator KYC Gateway — Live Verification Feed
        </div>
        <div style={{ fontSize: 11, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1.6 }}>
          NLA receives <strong style={{ color: "#8C8C8C" }}>tokenised verification results</strong> from operator onboarding systems — never raw biometric data. 
          The operator's system performs liveness detection, ID document scan, and age extraction; 
          GCRMS records the outcome and flags exceptions.
        </div>
      </div>

      {/* Scenario buttons */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {SCENARIOS.map(s => (
          <button
            key={s.label}
            onClick={() => run(s)}
            disabled={phase === "scanning"}
            style={{
              flex: 1, padding: "8px 10px", borderRadius: 7, border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.04)", color: "#A8A8A8", cursor: phase === "scanning" ? "not-allowed" : "pointer",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fontWeight: 600,
              transition: "all 0.2s", textAlign: "center",
            }}
          >{s.label}</button>
        ))}
      </div>

      {/* Scan animation / result */}
      <div style={{
        minHeight: 180, borderRadius: 10, border: "1px solid rgba(255,255,255,0.06)",
        background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", position: "relative",
      }}>
        {phase === "idle" && (
          <div style={{ textAlign: "center", color: "#595959" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>◉</div>
            <div style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" }}>Select a verification scenario above</div>
          </div>
        )}

        {phase === "scanning" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#40A9FF", fontFamily: "'IBM Plex Mono', monospace", marginBottom: 14 }}>
              PROCESSING VERIFICATION TOKEN...
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 12 }}>
              {["Liveness check", "ID scan", "Age extraction", "MoJ cross-ref"].map((step, i) => (
                <div key={step} style={{
                  fontSize: 9, fontFamily: "'IBM Plex Mono', monospace", color: "#40A9FF",
                  background: "rgba(64,169,255,0.1)", padding: "3px 8px", borderRadius: 3,
                  animation: `fade-in 0.3s ease ${i * 0.4}s both`,
                }}>{step}</div>
              ))}
            </div>
            <div style={{
              width: 200, height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, margin: "0 auto", overflow: "hidden",
            }}>
              <div style={{
                height: "100%", background: "#40A9FF", borderRadius: 2,
                animation: "scan-bar 2.2s ease forwards",
              }} />
            </div>
            <style>{`
              @keyframes scan-bar { from{width:0%} to{width:100%} }
              @keyframes fade-in { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
            `}</style>
          </div>
        )}

        {(phase === "match" || phase === "reject") && demo && (
          <div style={{ width: "100%", padding: "20px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", marginBottom: 4 }}>
                  VERIFICATION RESULT — {new Date().toLocaleTimeString()}
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: resultColors[demo.result] }}>{demo.result}</div>
                <div style={{ fontSize: 11, color: "#8C8C8C", marginTop: 2 }}>Ref: {demo.idRef}</div>
              </div>
              <div style={{
                width: 48, height: 48, borderRadius: 10,
                background: `${resultColors[demo.result]}20`,
                border: `2px solid ${resultColors[demo.result]}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 22,
              }}>
                {demo.result === "VERIFIED" ? "✓" : demo.result === "MANUAL REVIEW" ? "?" : "✗"}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
              {[
                { label: "Subject", value: demo.name },
                { label: "Age", value: `${demo.age} yrs`, color: demo.age < 18 ? "#FF4D4F" : demo.age < 21 ? "#FAAD14" : "#52C41A" },
                { label: "Liveness Score", value: `${demo.livenessScore}%`, color: "#52C41A" },
                { label: "ID Match", value: `${demo.matchScore}%`, color: demo.matchScore > 90 ? "#52C41A" : "#FAAD14" },
              ].map(item => (
                <div key={item.label} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 6, padding: "8px 10px" }}>
                  <div style={{ fontSize: 9, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", marginBottom: 3 }}>{item.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: item.color || "#D9D9D9", fontFamily: "'IBM Plex Mono', monospace" }}>{item.value}</div>
                </div>
              ))}
            </div>

            <div style={{
              fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", color: "#595959",
              padding: "8px 12px", background: "rgba(255,255,255,0.03)", borderRadius: 6,
            }}>
              {demo.result === "VERIFIED" && "✓ Identity confirmed. Age ≥ 18 verified. Account cleared for gaming activity. Token logged to NLA audit trail."}
              {demo.result === "MANUAL REVIEW" && "⚠ Age confidence below 70% threshold. Account flagged for manual review. Gaming activity restricted pending human verification."}
              {demo.result === "REJECTED" && "✗ Subject identified as underage (age 15). Registration blocked. Event logged to NLA Underage Access Register. Operator notified."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── OPERATOR ROW ─────────────────────────────────────────────────────────────

function OperatorRow({ op, selected, onClick }) {
  const cfg = programConfig[op.kycProgram];
  const isSelected = selected?.id === op.id;

  return (
    <div onClick={onClick} style={{
      display: "grid", gridTemplateColumns: "200px 120px 1fr 80px 80px 80px",
      gap: 12, padding: "12px 14px", cursor: "pointer", borderRadius: 8,
      background: isSelected ? "rgba(64,169,255,0.06)" : "transparent",
      border: isSelected ? "1px solid rgba(64,169,255,0.3)" : "1px solid transparent",
      transition: "all 0.15s", alignItems: "center",
      borderBottom: "1px solid rgba(255,255,255,0.04)",
    }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#D9D9D9" }}>{op.name}</div>
        <div style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace" }}>{op.type}</div>
      </div>
      <span style={{
        fontSize: 10, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace",
        color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
        padding: "3px 8px", borderRadius: 4, textAlign: "center", display: "inline-block",
      }}>{op.kycProgram}</span>
      <RateBar value={op.verifiedPlayerRate} />
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: op.unverifiedActiveAccounts > 500 ? "#FF4D4F" : op.unverifiedActiveAccounts > 100 ? "#FAAD14" : "#52C41A", fontFamily: "'IBM Plex Mono', monospace" }}>
          {op.unverifiedActiveAccounts.toLocaleString()}
        </div>
      </div>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: op.underageFlags > 0 ? "#FF4D4F" : "#52C41A", fontFamily: "'IBM Plex Mono', monospace" }}>
          {op.underageFlags}
        </div>
      </div>
      <div style={{ fontSize: 11, color: (auditConfig[op.lastAuditResult] || {}).color || "#8C8C8C", fontFamily: "'IBM Plex Mono', monospace", textAlign: "center", lineHeight: 1.3 }}>
        {op.lastAuditResult.split(" ")[0]}
      </div>
    </div>
  );
}

// ─── DETAIL PANEL ─────────────────────────────────────────────────────────────

function DetailPanel({ op }) {
  const cfg = programConfig[op.kycProgram];

  return (
    <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#F0F0F0", marginBottom: 3 }}>{op.name}</div>
          <div style={{ fontSize: 11, color: "#595959", fontFamily: "'IBM Plex Mono', monospace" }}>{op.ageVerificationMethod}</div>
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`, padding: "4px 12px", borderRadius: 20, height: "fit-content" }}>
          {op.kycProgram}
        </span>
      </div>

      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 18 }}>
        {[
          { label: "KYC Provider", value: op.kycProvider },
          { label: "Policy Version", value: op.policyVersion },
          { label: "Avg Onboarding", value: op.avgOnboardingTime },
          { label: "Last Audit", value: op.lastAuditDate },
          { label: "Audit Result", value: op.lastAuditResult.split("—")[0].trim(), color: (auditConfig[op.lastAuditResult] || {}).color },
          { label: "Underage Flags", value: op.underageFlags, color: op.underageFlags > 0 ? "#FF4D4F" : "#52C41A" },
        ].map(item => (
          <div key={item.label} style={{ background: "rgba(255,255,255,0.03)", borderRadius: 7, padding: "9px 12px" }}>
            <div style={{ fontSize: 9, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{item.label}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: item.color || "#D9D9D9" }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Controls checklist */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Required Controls</div>
        {op.controls.map(c => (
          <div key={c.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
            <span style={{ fontSize: 13, color: c.status ? "#52C41A" : "#FF4D4F" }}>{c.status ? "✓" : "✗"}</span>
            <span style={{ fontSize: 11, color: c.status ? "#A8A8A8" : "#595959" }}>{c.label}</span>
            {!c.status && <span style={{ marginLeft: "auto", fontSize: 10, color: "#FF7A00", background: "rgba(255,122,0,0.1)", padding: "1px 7px", borderRadius: 3 }}>NON-COMPLIANT</span>}
          </div>
        ))}
      </div>

      {/* Age breakdown bar chart */}
      {op.ageBreakdown.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Player Age Group Verification</div>
          <ResponsiveContainer width="100%" height={110}>
            <BarChart data={op.ageBreakdown} margin={{ top: 0, right: 5, left: -30, bottom: 0 }}>
              <XAxis dataKey="group" tick={{ fontSize: 9, fill: "#595959", fontFamily: "'IBM Plex Mono', monospace" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: "#595959" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#1A1A2E", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }} />
              <Bar dataKey="count" name="Total" fill="rgba(255,255,255,0.08)" radius={[2, 2, 0, 0]} />
              <Bar dataKey="verified" name="Verified" fill="#40A9FF" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Incidents */}
      {op.recentIncidents.length > 0 && (
        <div style={{ marginTop: 16, background: "rgba(255,77,79,0.06)", border: "1px solid rgba(255,77,79,0.2)", borderRadius: 8, padding: "12px 14px" }}>
          <div style={{ fontSize: 10, color: "#FF4D4F", fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}>⚠ Recent Incidents</div>
          {op.recentIncidents.map((inc, i) => (
            <div key={i} style={{ fontSize: 11, color: "#FF7A00", marginBottom: 5 }}>› {inc}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────

export default function NLA_KYCMonitor() {
  const [selected, setSelected] = useState(OPERATORS[1]); // Lucky Star pre-selected
  const [view, setView] = useState("overview");

  const totalUnverified = OPERATORS.reduce((s, o) => s + o.unverifiedActiveAccounts, 0);
  const totalUnderage = OPERATORS.reduce((s, o) => s + o.underageFlags, 0);
  const implemented = OPERATORS.filter(o => o.kycProgram === "IMPLEMENTED").length;
  const avgRate = (OPERATORS.reduce((s, o) => s + o.verifiedPlayerRate, 0) / OPERATORS.length).toFixed(1);

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0D0D1A 0%, #0A1628 50%, #0D0D1A 100%)",
      fontFamily: "'IBM Plex Sans', 'Segoe UI', sans-serif",
      color: "#D9D9D9", padding: 24,
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&family=IBM+Plex+Sans:wght@400;500;600;700;800&display=swap'); *{box-sizing:border-box;} ::-webkit-scrollbar{width:6px;} ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:3px;}`}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ width: 6, height: 28, background: "linear-gradient(180deg, #722ED1, #391085)", borderRadius: 3 }} />
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#F0F0F0", letterSpacing: "-0.02em" }}>
              KYC & Age Verification Oversight
            </h1>
          </div>
          <div style={{ fontSize: 11, color: "#595959", marginLeft: 16, fontFamily: "'IBM Plex Mono', monospace" }}>
            NLA · Operator Programme Compliance · Dec 28, 2025
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {[
            { id: "overview", label: "⊞ Overview" },
            { id: "verification", label: "◉ Verification Demo" },
          ].map(v => (
            <button key={v.id} onClick={() => setView(v.id)} style={{
              background: view === v.id ? "rgba(114,46,209,0.15)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${view === v.id ? "rgba(114,46,209,0.5)" : "rgba(255,255,255,0.08)"}`,
              color: view === v.id ? "#722ED1" : "#8C8C8C", cursor: "pointer",
              padding: "7px 14px", borderRadius: 7, fontSize: 11, fontWeight: 600,
              fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.07em",
              transition: "all 0.2s",
            }}>{v.label}</button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Avg KYC Compliance", value: `${avgRate}%`, color: parseFloat(avgRate) >= 80 ? "#52C41A" : "#FAAD14" },
          { label: "Fully Implemented", value: `${implemented}/${OPERATORS.length}`, color: "#40A9FF" },
          { label: "Unverified Active Accounts", value: totalUnverified.toLocaleString(), color: totalUnverified > 1000 ? "#FF4D4F" : "#FAAD14" },
          { label: "Underage Flags (30d)", value: totalUnderage, color: totalUnderage > 0 ? "#FF4D4F" : "#52C41A" },
        ].map(item => (
          <div key={item.label} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: "14px 18px" }}>
            <div style={{ fontSize: 10, color: "#595959", marginBottom: 6, fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.07em" }}>{item.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: item.color, fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>{item.value}</div>
          </div>
        ))}
      </div>

      {view === "overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 16 }}>
          <div>
            {/* Table */}
            <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "200px 120px 1fr 80px 80px 80px", gap: 12, padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                {["Operator", "KYC Program", "Verified Player Rate", "Unverified", "Underage", "Audit"].map(h => (
                  <div key={h} style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
                ))}
              </div>
              <div style={{ padding: "6px 6px" }}>
                {OPERATORS.map(op => (
                  <OperatorRow key={op.id} op={op} selected={selected} onClick={() => setSelected(op)} />
                ))}
              </div>
            </div>

            {/* NLA framing note */}
            <div style={{ background: "rgba(114,46,209,0.06)", border: "1px solid rgba(114,46,209,0.2)", borderRadius: 10, padding: "14px 18px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#722ED1", fontFamily: "'IBM Plex Mono', monospace", marginBottom: 6, textTransform: "uppercase" }}>
                NLA Regulatory Position
              </div>
              <div style={{ fontSize: 12, color: "#8C8C8C", lineHeight: 1.7 }}>
                Under Section 14(c) of the NLA Act 2014, all licensed operators are required to maintain and operate a compliant player verification programme. NLA does not conduct KYC directly — operators must demonstrate that their onboarding systems meet the minimum technical standards set by NLA. This panel provides NLA compliance officers with real-time visibility into each operator's programme status, verification rates, and incident history, enabling targeted enforcement action without requiring direct access to player data.
              </div>
            </div>
          </div>

          <DetailPanel op={selected} />
        </div>
      )}

      {view === "verification" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <FacialScanDemo />
          <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#F0F0F0", marginBottom: 16 }}>How NLA Receives Verification Data</div>
            {[
              { step: "01", title: "Player registers on operator platform", detail: "Operator's system captures National ID document, selfie, and date of birth." },
              { step: "02", title: "Operator's KYC system processes locally", detail: "Liveness detection, face-ID match, and age extraction run on the operator's infrastructure. Raw biometric data never leaves the operator." },
              { step: "03", title: "Verification token sent to GCRMS", detail: "A structured result token is sent: VERIFIED / MANUAL REVIEW / REJECTED, with confidence scores and age band — no personal data." },
              { step: "04", title: "NLA logs outcome to audit trail", detail: "Every verification event is timestamped, operator-tagged, and stored in the immutable NLA audit register." },
              { step: "05", title: "Exceptions trigger NLA action", detail: "Underage flags, low-confidence results, or bypass patterns immediately create a compliance alert in the NLA enforcement queue." },
            ].map((item, i) => (
              <div key={item.step} style={{ display: "flex", gap: 14, marginBottom: 18 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                  background: "rgba(114,46,209,0.15)", border: "1px solid rgba(114,46,209,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 700, color: "#722ED1", fontFamily: "'IBM Plex Mono', monospace",
                }}>{item.step}</div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#D9D9D9", marginBottom: 3 }}>{item.title}</div>
                  <div style={{ fontSize: 11, color: "#595959", lineHeight: 1.5 }}>{item.detail}</div>
                </div>
              </div>
            ))}
            <div style={{ background: "rgba(82,196,26,0.06)", border: "1px solid rgba(82,196,26,0.2)", borderRadius: 8, padding: "12px 14px", marginTop: 8 }}>
              <div style={{ fontSize: 11, color: "#52C41A", fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, marginBottom: 4 }}>Data Sovereignty</div>
              <div style={{ fontSize: 11, color: "#8C8C8C", lineHeight: 1.5 }}>NLA never stores biometric data. Only verification outcomes and statistical aggregates are retained, consistent with Liberia's data protection framework.</div>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, textAlign: "center", fontSize: 10, color: "#333", fontFamily: "'IBM Plex Mono', monospace" }}>
        GCRMS · XI CONSULT CORP · CONFIDENTIAL — NLA DEMO BUILD
      </div>
    </div>
  );
}